import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  Heart,
  Calendar,
  Camera,
  Users,
  Bell,
  CheckCircle2,
  ArrowRight,
  Sparkles,
  Star,
  Building2,
  FileSpreadsheet,
  CreditCard,
  Clock,
  Shield,
  Smartphone
} from 'lucide-react';

const photographerFeatures = [
  {
    icon: Calendar,
    title: 'Akıllı Takvim',
    description: 'Düğün tarihlerini takvimde görün, çakışmaları önleyin.'
  },
  {
    icon: Camera,
    title: 'Galeri Yönetimi',
    description: 'Fotoğraf galerisi oluşturun, müşterilerin seçim yapmasını sağlayın.'
  },
  {
    icon: Users,
    title: 'Müşteri Takibi',
    description: 'Tüm müşteri bilgilerini ve anlaşmaları tek yerden yönetin.'
  },
  {
    icon: Bell,
    title: 'SMS & WhatsApp',
    description: 'Otomatik hatırlatmalar ve anlaşma bilgileri gönderin.'
  }
];

const salonFeatures = [
  {
    icon: Building2,
    title: 'Çoklu Salon Yönetimi',
    description: 'Birden fazla salonu aynı sistemden kolayca yönetin.'
  },
  {
    icon: Calendar,
    title: '4 Adımlı Rezervasyon',
    description: 'Salon seç → Tarih seç → Müşteri bilgisi → Paket oluştur.'
  },
  {
    icon: CreditCard,
    title: 'Ödeme & Borç Takibi',
    description: 'Kapora, taksit ve kalan borç takibini otomatik yapın.'
  },
  {
    icon: FileSpreadsheet,
    title: 'Excel Yedekleme',
    description: 'Tüm verilerinizi Excel formatında yedekleyin.'
  }
];

const commonBenefits = [
  'Sınırsız müşteri kaydı',
  'Türkçe takvim görünümü',
  'Detaylı ödeme takibi',
  'Mobil uyumlu tasarım (PWA)',
  'Excel veri yedekleme',
  'Güvenli bulut depolama'
];

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <Heart className="h-7 w-7 text-primary" />
              <span className="text-xl font-bold gradient-text">WeddingTakip</span>
            </div>
            <div className="flex items-center gap-3">
              <Link to="/giris">
                <Button variant="ghost" data-testid="login-nav-btn">Giriş Yap</Button>
              </Link>
              <Link to="/kayit">
                <Button data-testid="register-nav-btn">
                  Ücretsiz Başla
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-8 animate-fade-in">
            <Sparkles className="h-4 w-4" />
            Fotoğrafçılar & Düğün Salonları için
          </div>
          
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 animate-fade-in stagger-1">
            Düğün İşinizi
            <br />
            <span className="gradient-text">Tek Platformda Yönetin</span>
          </h1>
          
          <p className="text-lg sm:text-xl text-muted-foreground max-w-3xl mx-auto mb-10 animate-fade-in stagger-2">
            <strong>Fotoğrafçılar</strong> için müşteri, galeri ve takvim yönetimi. 
            <strong> Düğün Salonları</strong> için rezervasyon, ödeme ve çoklu salon takibi.
            Her şey tek çatı altında!
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-in stagger-3">
            <Link to="/kayit">
              <Button size="lg" className="text-base px-8" data-testid="hero-cta-btn">
                Hemen Başla
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/giris">
              <Button variant="outline" size="lg" className="text-base px-8">
                Giriş Yap
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* User Types Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-muted/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Kim İçin?</h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              İki farklı kullanıcı tipi için özel tasarlanmış paneller
            </p>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Fotoğrafçılar */}
            <div className="p-8 rounded-3xl bg-card border-2 border-primary/20 hover:border-primary/50 transition-all duration-300">
              <div className="flex items-center gap-3 mb-6">
                <div className="h-14 w-14 rounded-2xl bg-primary/10 flex items-center justify-center">
                  <Camera className="h-7 w-7 text-primary" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold">Fotoğrafçılar</h3>
                  <p className="text-muted-foreground">Profesyonel stüdyolar için</p>
                </div>
              </div>
              
              <div className="space-y-4">
                {photographerFeatures.map((feature) => (
                  <div key={feature.title} className="flex items-start gap-3">
                    <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <feature.icon className="h-4 w-4 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-semibold">{feature.title}</h4>
                      <p className="text-sm text-muted-foreground">{feature.description}</p>
                    </div>
                  </div>
                ))}
              </div>
              
              <Link to="/kayit" className="block mt-8">
                <Button className="w-full" size="lg">
                  Fotoğrafçı Olarak Kayıt Ol
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>

            {/* Düğün Salonları */}
            <div className="p-8 rounded-3xl bg-card border-2 border-purple-500/20 hover:border-purple-500/50 transition-all duration-300">
              <div className="flex items-center gap-3 mb-6">
                <div className="h-14 w-14 rounded-2xl bg-purple-500/10 flex items-center justify-center">
                  <Building2 className="h-7 w-7 text-purple-500" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold">Düğün Salonları</h3>
                  <p className="text-muted-foreground">Salon sahipleri için</p>
                </div>
              </div>
              
              <div className="space-y-4">
                {salonFeatures.map((feature) => (
                  <div key={feature.title} className="flex items-start gap-3">
                    <div className="h-8 w-8 rounded-lg bg-purple-500/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <feature.icon className="h-4 w-4 text-purple-500" />
                    </div>
                    <div>
                      <h4 className="font-semibold">{feature.title}</h4>
                      <p className="text-sm text-muted-foreground">{feature.description}</p>
                    </div>
                  </div>
                ))}
              </div>
              
              <Link to="/kayit" className="block mt-8">
                <Button className="w-full bg-purple-500 hover:bg-purple-600" size="lg">
                  Salon Sahibi Olarak Kayıt Ol
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6">
                Neden <span className="gradient-text">WeddingTakip</span>?
              </h2>
              <p className="text-muted-foreground text-lg mb-8">
                Yıllardır düğün sektöründe çalışan profesyonellerin ihtiyaçlarına 
                göre tasarlandı. Basit, hızlı ve etkili.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {commonBenefits.map((benefit) => (
                  <div key={benefit} className="flex items-center gap-3">
                    <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0" />
                    <span className="text-sm">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="p-6 rounded-2xl bg-primary/5 border border-primary/20 text-center">
                <Clock className="h-8 w-8 text-primary mx-auto mb-3" />
                <p className="text-3xl font-bold">%70</p>
                <p className="text-sm text-muted-foreground">Zaman Tasarrufu</p>
              </div>
              <div className="p-6 rounded-2xl bg-purple-500/5 border border-purple-500/20 text-center">
                <Shield className="h-8 w-8 text-purple-500 mx-auto mb-3" />
                <p className="text-3xl font-bold">%100</p>
                <p className="text-sm text-muted-foreground">Güvenli Veri</p>
              </div>
              <div className="p-6 rounded-2xl bg-green-500/5 border border-green-500/20 text-center">
                <Smartphone className="h-8 w-8 text-green-500 mx-auto mb-3" />
                <p className="text-3xl font-bold">PWA</p>
                <p className="text-sm text-muted-foreground">Mobil Uygulama</p>
              </div>
              <div className="p-6 rounded-2xl bg-amber-500/5 border border-amber-500/20 text-center">
                <div className="flex justify-center gap-0.5 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 fill-amber-500 text-amber-500" />
                  ))}
                </div>
                <p className="text-3xl font-bold">4.9</p>
                <p className="text-sm text-muted-foreground">Kullanıcı Puanı</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-primary/10 via-transparent to-purple-500/10">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">
            Hemen Ücretsiz Başlayın
          </h2>
          <p className="text-muted-foreground text-lg mb-8">
            Kredi kartı gerektirmez. Dakikalar içinde sistemi kullanmaya başlayın.
            <br />
            <span className="font-medium">Fotoğrafçı veya Salon Sahibi - Seçim sizin!</span>
          </p>
          <Link to="/kayit">
            <Button size="lg" className="text-base px-10" data-testid="cta-register-btn">
              Ücretsiz Hesap Oluştur
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 border-t border-border">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Heart className="h-5 w-5 text-primary" />
              <span className="font-semibold">WeddingTakip</span>
            </div>
            <p className="text-sm text-muted-foreground">
              © 2025 WeddingTakip. Tüm hakları saklıdır.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
